import type { SegmentBrowserAnalytics } from '@hellodarwin/app-core/lib/providers/segment-provider';
import { Profile } from '@hellodarwin/core/lib/features/entities';
import TrackingEvent from '@hellodarwin/core/lib/features/enums/tracking-event';
import {
  OnboardingAnalyticsData,
  OnboardingData,
  OnboardingStep,
} from '../../modules/onboarding-flow/types/onboarding';
import {
  CLIENT_APP_CREATION_SOURCE,
  CLIENT_APP_PAGE_PROPERTIES,
  CLIENT_APP_SOURCE_SURFACE,
} from './client-analytics-constants';

type BaseAnalyticsProperties = {
  creation_source: typeof CLIENT_APP_CREATION_SOURCE;
  source_surface: typeof CLIENT_APP_SOURCE_SURFACE;
  id: string;
};

export type ALCGrantValidationSurface =
  | 'funding_explorer_grant_card'
  | 'funding_explorer_single_grant';

export interface ALCGrantValidationCtaClickedProperties {
  grant_id: string;
  grant_filing_request_id_present: boolean;
  stripe_subscription_id: string;
  alc_validation_cohort_match: true;
  remaining_validation_quota: number;
  locale: 'en' | 'fr';
  surface: ALCGrantValidationSurface;
  form_language: 'en' | 'fr';
}

const isDev = process.env.NODE_ENV === 'development';

const logAnalytics = (event: string, properties?: any) => {
  if (isDev) {
    console.warn(`[Analytics] ${event}`, properties);
  }
};

export default class ClientAnalytics {
  private readonly analytics: SegmentBrowserAnalytics | undefined;
  private readonly clientProfile: Profile;
  private readonly baseAnalyticsProperties: BaseAnalyticsProperties;

  constructor(
    analytics: SegmentBrowserAnalytics | undefined,
    clientProfile: Profile,
  ) {
    this.analytics = analytics;
    this.clientProfile = clientProfile;
    this.baseAnalyticsProperties = {
      creation_source: CLIENT_APP_CREATION_SOURCE,
      source_surface: CLIENT_APP_SOURCE_SURFACE,
      id: clientProfile.contact_id,
    };
  }

  isReady = () => Boolean(this.analytics);

  track = (event: string, properties?: any) => {
    const analyticsProperties = {
      ...this.clientProfile,
      ...properties,
      ...this.baseAnalyticsProperties,
    };
    this.analytics?.track(event, analyticsProperties);
    logAnalytics(event, analyticsProperties);
  };

  identify = () => {
    const analyticsProperties = {
      email: this.clientProfile.contact_email,
      name: this.clientProfile.contact_name,
      ...this.clientProfile,
      source_surface: CLIENT_APP_SOURCE_SURFACE,
    };
    this.analytics?.identify(
      this.clientProfile.contact_id,
      analyticsProperties,
    );
    logAnalytics('Identify', analyticsProperties);
  };

  page = () => {
    // The undefined-placeholder form drops these properties in live Segment.
    this.analytics?.page(CLIENT_APP_PAGE_PROPERTIES);
    logAnalytics('Page', CLIENT_APP_PAGE_PROPERTIES);
  };

  checkoutPlansViewed = () => {
    this.track(TrackingEvent.CheckoutPlansViewed);
  };

  projectClicked = (properties: { rfpId: string }) => {
    this.track(TrackingEvent.ProjectClicked, properties);
  };

  providerClicked = (properties: { providerId: string; rfpId: string }) => {
    this.track(TrackingEvent.ProviderClicked, properties);
  };

  providerContactClicked = (properties: {
    clicked_provider_id: string;
    clicked_provider_contact_id: string;
    clicked_project_id: string;
  }) => {
    this.track(TrackingEvent.ProviderContactClicked, properties);
  };

  grantApplyNowClicked = (properties: { clicked_grant_id: string }) => {
    this.track(TrackingEvent.GrantApplyNowClicked, properties);
  };

  alcGrantValidationCtaClicked = (
    properties: ALCGrantValidationCtaClickedProperties,
  ) => {
    this.track(TrackingEvent.ALCGrantValidationCtaClicked, {
      company_id: this.clientProfile.company_id,
      contact_id: this.clientProfile.contact_id,
      ...properties,
    });
  };

  onboardingStarted = (data: OnboardingData, flowStartingPoint: string) => {
    this.track(TrackingEvent.OnboardingStarted, {
      ...data,
      ...data.formData,
      flowStartingPoint,
    });
  };

  onboardingStepCompleted = (
    properties: { step_key: OnboardingStep },
    data: OnboardingData,
  ) => {
    this.track(TrackingEvent.OnboardingStepCompleted, {
      ...properties,
      ...data,
    });
  };

  onboardingCompleted = (
    data: OnboardingAnalyticsData,
    step_key: OnboardingStep,
    status: string = 'completed',
    returnTo: string = 'dashboard',
  ) => {
    this.track(TrackingEvent.OnboardingCompleted, {
      ...data,
      ...data.formData,
      status,
      step_key,
      returnTo,
    });
  };

  openMeetingModalClicked = (type: string) => {
    this.track(TrackingEvent.OpenMeetingModalClicked, {
      ...this.clientProfile,
      talkingTo: type,
    });
  };
}
